import React, { useEffect, useRef, useState } from "react";
import AppShell from "../components/AppShell";
import { api, API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Database, Download, Upload, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

const Stat = ({ label, value }) => (
  <div className="bg-slate-50 border border-border rounded-md p-3">
    <div className="text-[10.5px] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
    <div className="font-mono-fin text-xl font-semibold mt-1">{value}</div>
  </div>
);

export default function Settings() {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", role: "finance" });
  const importRef = useRef();

  const load = async () => {
    const [u, s] = await Promise.all([
      api.get("/users").then((r) => r.data),
      api.get("/admin/stats").then((r) => r.data),
    ]);
    setUsers(u); setStats(s);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.name || !form.email) return toast.error("Name and email required");
    await api.post("/users", form);
    toast.success("User added");
    setOpen(false); setForm({ name: "", email: "", role: "finance" });
    load();
  };

  const del = async (id) => {
    if (!window.confirm("Delete user?")) return;
    await api.delete(`/users/${id}`);
    load();
  };

  const exportAll = () => { window.location.href = `${API}/admin/export-all`; };

  const importAll = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const mode = window.confirm("Use MERGE mode (OK)? MERGE upserts by id. Cancel = REPLACE mode (wipes collections first).") ? "merge" : "replace";
    if (mode === "replace" && !window.confirm("⚠️  REPLACE will WIPE all collections before import. Confirm?")) {
      e.target.value = ""; return;
    }
    try {
      const text = await f.text();
      const data = JSON.parse(text);
      const r = await api.post("/admin/import-all", { data, mode });
      toast.success(`Imported (${mode}): ${Object.entries(r.data.summary).map(([k, v]) => `${k}:${Object.values(v)[0]}`).join(", ")}`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      e.target.value = "";
    }
  };

  return (
    <AppShell
      title="Settings"
      subtitle="Manual user management · Admin / Finance / Read-Only roles"
      actions={
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="bg-[#0A4C95] hover:bg-[#083d79]" data-testid="new-user-btn"><Plus className="w-4 h-4 mr-1" /> New User</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
            <div className="space-y-3 py-2">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin — Full access</SelectItem>
                    <SelectItem value="finance">Finance — Data entry, no payroll</SelectItem>
                    <SelectItem value="readonly">Read-Only — Dashboard views</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button><Button className="bg-[#0A4C95] hover:bg-[#083d79]" onClick={save} data-testid="user-save-btn">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      }
    >
      {/* Data Management */}
      <div className="bg-white border border-border rounded-md shadow-sm p-6 mb-6" data-testid="admin-data-panel">
        <div className="flex items-center gap-2 mb-1">
          <Database className="w-5 h-5 text-[#0A4C95]" />
          <h3 className="text-base font-semibold">Data Management</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">Export for backup or migration to production. Import to restore a previous snapshot.</p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-5">
            <Stat label="Users" value={stats.users} />
            <Stat label="Sites" value={stats.sites} />
            <Stat label="CTAs" value={stats.ctas} />
            <Stat label="Employees" value={stats.employees} />
            <Stat label="Timesheets" value={stats.timesheets} />
            <Stat label="Revenues" value={stats.revenues} />
            <Stat label="Expenses" value={stats.expenses} />
            <Stat label="Reports" value={stats.reports} />
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={exportAll} data-testid="admin-export-btn" className="bg-[#0A4C95] hover:bg-[#083d79]">
            <Download className="w-4 h-4 mr-1.5" /> Export All (JSON)
          </Button>
          <input ref={importRef} type="file" accept=".json" hidden onChange={importAll} data-testid="admin-import-input" />
          <Button variant="outline" onClick={() => importRef.current?.click()} data-testid="admin-import-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Import Snapshot
          </Button>
          <div className="text-xs text-slate-500 ml-2">
            {stats?.demo_seed_enabled ? (
              <Badge variant="outline" className="bg-amber-50 text-amber-800 border-amber-200"><AlertTriangle className="w-3 h-3 mr-1 inline" /> Demo seed enabled (dev mode)</Badge>
            ) : (
              <Badge variant="outline" className="bg-emerald-50 text-emerald-800 border-emerald-200">Production safe (seed disabled)</Badge>
            )}
          </div>
        </div>

        <div className="mt-4 text-xs text-slate-500 bg-slate-50 p-3 rounded border border-slate-200">
          <strong>Dev → Prod tip:</strong> Click <em>Export All</em> here, then on the production instance open Settings and use <em>Import Snapshot</em> with mode MERGE (recommended) or REPLACE (wipes first). Full docs at <code className="bg-white px-1 py-0.5 rounded">/app/scripts/README.md</code>.
        </div>
      </div>

      {/* Users */}
      <div className="bg-white border border-border rounded-md shadow-sm overflow-auto">
        <table className="w-full text-sm stryde-table">
          <thead><tr><th className="text-left px-4 py-2.5">Name</th><th className="text-left px-4 py-2.5">Email</th><th className="text-left px-4 py-2.5">Role</th><th className="text-left px-4 py-2.5">Created</th><th></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-t border-slate-100">
                <td className="px-4 py-2.5 font-semibold">{u.name}</td>
                <td className="px-4 py-2.5 text-slate-600">{u.email}</td>
                <td className="px-4 py-2.5"><Badge variant="outline" className={u.role === "admin" ? "bg-blue-50 text-blue-800 border-blue-200" : u.role === "finance" ? "bg-orange-50 text-orange-800 border-orange-200" : "bg-slate-100 text-slate-700"}>{u.role}</Badge></td>
                <td className="px-4 py-2.5 text-xs text-slate-500">{new Date(u.created_at).toLocaleDateString()}</td>
                <td className="px-4 py-2.5 text-right"><Button size="icon" variant="ghost" onClick={() => del(u.id)}><Trash2 className="w-4 h-4 text-red-500" /></Button></td>
              </tr>
            ))}
            {users.length === 0 && <tr><td colSpan={5} className="text-center py-10 text-slate-500">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </AppShell>
  );
}
